/**
 * Product Business Rules Constants
 *
 * Shared constants for product management business rules.
 * Used by both frontend and backend for consistent rule enforcement.
 *
 * DRY Compliance: Single source of truth for product rules
 * Rule 15 Compliance: Root cause solution - no scattered business logic
 *
 * @module @axon/common/constants/product-rules
 */

/**
 * Categories that require special tracking (lot/serial/expiry control)
 */
export const REGULATED_CATEGORIES = [
  'PHARMACEUTICALS',
  'FOOD_PERISHABLE',
  'MEDICAL_DEVICES',
  'CHEMICALS',
  'ELECTRONICS_HIGH_VALUE',
] as const;

/**
 * High value threshold for automatic serialization (USD)
 * Products with unit cost >= this threshold will be automatically serialized
 */
export const HIGH_VALUE_THRESHOLD = 1000;

/**
 * Default ABC classification thresholds (annual value in USD)
 * Class A: Top 20% of products by value
 * Class B: Middle 30% of products by value
 * Class C: Bottom 50% of products by value
 */
export const ABC_THRESHOLDS = {
  /** Annual value threshold for Class A classification */
  CLASS_A_THRESHOLD: 50000, // $50,000 annual value
  /** Annual value threshold for Class B classification */
  CLASS_B_THRESHOLD: 10000, // $10,000 annual value
} as const;

/**
 * Default reorder point multiplier (based on lead time)
 * reorder_point = average_daily_usage * lead_time_days * multiplier
 */
export const REORDER_POINT_MULTIPLIER = 1.5;

/**
 * Default lead time in days for reorder calculation
 */
export const DEFAULT_LEAD_TIME_DAYS = 7;

/**
 * Expiry alert thresholds (days before expiry)
 */
export const EXPIRY_ALERT_THRESHOLDS = {
  /** Critical: expired or expiring today */
  CRITICAL: 0,
  /** Warning: expiring within 7 days */
  WARNING: 7,
  /** Info: expiring within 30 days */
  INFO: 30,
} as const;

/**
 * Category codes that require expiry control by default
 */
export const EXPIRY_REQUIRED_CATEGORIES = [
  'PHARMACEUTICALS',
  'FOOD_PERISHABLE',
  'MEDICAL_DEVICES',
] as const;

/**
 * Category codes that require lot tracking by default
 */
export const LOT_REQUIRED_CATEGORIES = [
  'PHARMACEUTICALS',
  'FOOD_PERISHABLE',
  'MEDICAL_DEVICES',
  'CHEMICALS',
] as const;

/**
 * Category codes that require serial tracking by default
 */
export const SERIAL_REQUIRED_CATEGORIES = ['ELECTRONICS_HIGH_VALUE', 'MEDICAL_DEVICES'] as const;
